import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { catchError, map, Observable, of } from 'rxjs';

import { OktaInterfaceService } from '@shure/cloud/shared/okta/data-access';
import { OrganizationsApiService } from '@shure/cloud/shared/organization-switcher/data-access';
import { PrismSnackbarService } from '@shure/prism-angular-components';
import {
	generateColorFromName,
	generateInitials,
	OrganizationItem,
	OrganizationApi,
	VerificationStatus
} from '@shure/shared/angular/features/switch-organization';

import { OrganizationSwitcherService } from './organization-switcher.service';

@Injectable()
export class OrganizationApiService implements OrganizationApi {
	private readonly organizationsApiService = inject(OrganizationsApiService);
	private readonly oktaIntfService = inject(OktaInterfaceService);
	private readonly snackBarService = inject(PrismSnackbarService);
	private readonly translocoService = inject(TranslocoService);
	private readonly switchOrganizationService = inject(OrganizationSwitcherService);

	public getOrganizations(): Observable<OrganizationItem[]> {
		return this.organizationsApiService.getAllMyOrganizations$Response().pipe(
			map((response) => {
				const items = response.body?.body?.items ?? [];
				return items.map((org) => ({
					id: org.id ?? '',
					name: org.name ?? '',
					companyName: org.companyName || '--',
					verificationStatus:
						(org.verificationStatus as VerificationStatus) ?? VerificationStatus.NONVERIFIED,
					profileColor: generateColorFromName(org.name ?? ''),
					initials: generateInitials(org.name ?? '')
				}));
			}),
			catchError(() => {
				this.displaySnackbarError('cloud.shared.switch-org.loading-orgs-failure');
				return of([]);
			})
		);
	}

	public getCurrentOrgId$(): Observable<string> {
		return this.oktaIntfService.getOrganizationId$();
	}

	public switchOrganization(organizationId: string): void {
		this.switchOrganizationService.switchOrganization(organizationId);
	}

	private displaySnackbarError(key: string): void {
		this.snackBarService.open({
			message: this.translocoService.translate(key),
			actionText: this.translocoService.translate('common.snackbar.dismiss')
		});
	}
}
